import * as yup from 'yup';
import {
	extractConditionValue,
	FormConditionRule,
	normalizeConditionValue,
} from '../../BaseModule/Components/Form/Components/FormCondition/FormCondition';
import { Rule } from '../../BaseModule/Enums/Rule.enum';
import { WizardItem } from '../Interfaces/Wizard.interface';
import { WizardItemType } from '../Enums/WizardItemType.enum';

export interface WizardConditionSettings {
	equal?: string | boolean | number;
	in?: Array<string | number>;
	rules?: FormConditionRule[];
}

export type ConditionConfig = {
	controlName: string;
	equal?: string | boolean | number;
	in?: Array<string | number>;
	rules?: FormConditionRule[];
};

export type ConditionWizardItem = Extract<
	WizardItem,
	{ type: WizardItemType.CONDITION }
> & {
	value: string;
	settings: WizardConditionSettings;
};

const unique = (arr: string[]): string[] => Array.from(new Set(arr));

const toArrayUnknown = (v: unknown): unknown[] => (Array.isArray(v) ? v : [v]);

const normalizeScalar = (v: unknown): string | boolean | number | undefined => {
	if (
		typeof v === 'string' ||
		typeof v === 'boolean' ||
		typeof v === 'number'
	) {
		return normalizeConditionValue(v);
	}
	return normalizeConditionValue(undefined);
};

const evaluateRulesFromMap = (
	valuesByKey: Record<string, unknown>,
	rules?: FormConditionRule[],
): boolean => {
	if (!rules || rules.length === 0) return true;

	for (const ruleItem of rules) {
		const raw = valuesByKey[ruleItem.formControlKey];
		const realValue = normalizeScalar(extractConditionValue(raw) as unknown);

		const compareRaw: unknown = ruleItem.value;

		let ok = true;

		switch (ruleItem.rule) {
			case Rule.FILLED:
				ok = !!realValue;
				break;

			case Rule.EQUAL:
				ok = realValue === normalizeScalar(compareRaw);
				break;

			case Rule.NOT_EQUAL:
				ok = realValue !== normalizeScalar(compareRaw);
				break;

			case Rule.IN:
				ok =
					Array.isArray(compareRaw) &&
					compareRaw.map(normalizeScalar).includes(realValue);
				break;

			case Rule.STARTS_WITH: {
				const rv = String(realValue ?? '');
				ok = toArrayUnknown(compareRaw).some(v =>
					rv.startsWith(String(v ?? '')),
				);
				break;
			}

			case Rule.NOT_STARTS_WITH: {
				const rv = String(realValue ?? '');
				ok = toArrayUnknown(compareRaw).every(
					v => !rv.startsWith(String(v ?? '')),
				);
				break;
			}

			default:
				ok = true;
				break;
		}

		if (!ok) return false;
	}

	return true;
};

const isConditionMetFromMap = (
	valuesByKey: Record<string, unknown>,
	cfg: ConditionConfig,
): boolean => {
	const base = evaluateRulesFromMap(valuesByKey, cfg.rules);

	const watchedRaw = valuesByKey[cfg.controlName];
	const watched = normalizeScalar(extractConditionValue(watchedRaw) as unknown);

	const hasEqual = typeof cfg.equal !== 'undefined';
	const equalOk = hasEqual ? watched === normalizeScalar(cfg.equal) : true;

	const hasIn = typeof cfg.in !== 'undefined';
	const inOk = hasIn
		? (cfg.in ?? []).map(normalizeScalar).includes(watched)
		: true;

	return !!base && equalOk && inOk;
};

export const getConditionConfigFromItem = (
	item: ConditionWizardItem,
): ConditionConfig => {
	return {
		controlName: item.value,
		equal: item.settings.equal,
		in: item.settings.in,
		rules: item.settings.rules,
	};
};

export const applyConditions = (
	schema: yup.AnySchema,
	conditions: ConditionConfig[],
): yup.AnySchema => {
	if (!conditions || conditions.length === 0) return schema;

	return conditions.reduce((acc, cfg) => {
		const deps = unique([
			cfg.controlName,
			...(cfg.rules?.map(r => r.formControlKey) ?? []),
		]);

		return acc.when(deps, {
			is: (...depValues: unknown[]) => {
				const depMap: Record<string, unknown> = {};
				for (let i = 0; i < deps.length; i++) depMap[deps[i]] = depValues[i];
				return isConditionMetFromMap(depMap, cfg);
			},
			then: s => s,
			otherwise: () => yup.mixed().notRequired().nullable(true),
		}) as yup.AnySchema;
	}, schema);
};
